import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import {
  ChevronRight,
  ChevronLeft,
  Zap,
  TrendingUp,
  Package,
  Star,
  Users,
  Gift,
  Truck,
  ShieldCheck,
  Clock,
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Hero Carousel
const heroSlides = [
  {
    id: 1,
    image: "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Nouveautés Tech",
    subtitle: "Découvrez les dernières innovations",
    cta: "Explorer",
    link: "/catalogue?category=electronique",
    gradient: "from-blue-900/90 via-blue-900/70 to-transparent",
  },
  {
    id: 2,
    image: "https://images.pexels.com/photos/3965545/pexels-photo-3965545.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Mode & Style",
    subtitle: "Jusqu'à -50% sur une sélection",
    cta: "En profiter",
    link: "/catalogue?category=mode",
    gradient: "from-rose-900/90 via-rose-900/70 to-transparent",
  },
  {
    id: 3,
    image: "https://images.pexels.com/photos/1090638/pexels-photo-1090638.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Maison & Déco",
    subtitle: "Créez votre intérieur idéal",
    cta: "Découvrir",
    link: "/catalogue?category=maison",
    gradient: "from-amber-900/90 via-amber-900/70 to-transparent",
  },
];

const defaultCategories = [
  { id: "electronique", name: "Électronique", slug: "electronique", icon: "💻", image: "https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "mode", name: "Mode", slug: "mode", icon: "👕", image: "https://images.pexels.com/photos/934070/pexels-photo-934070.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "maison", name: "Maison", slug: "maison", icon: "🏠", image: "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "sport", name: "Sport", slug: "sport", icon: "⚽", image: "https://images.pexels.com/photos/2294361/pexels-photo-2294361.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "beaute", name: "Beauté", slug: "beaute", icon: "💄", image: "https://images.pexels.com/photos/2587370/pexels-photo-2587370.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "alimentation", name: "Alimentation", slug: "alimentation", icon: "🍎", image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "jouets", name: "Jouets", slug: "jouets", icon: "🎮", image: "https://images.pexels.com/photos/163036/mario-luigi-yoschi-figures-163036.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { id: "auto", name: "Auto", slug: "auto", icon: "🚗", image: "https://images.pexels.com/photos/3729464/pexels-photo-3729464.jpeg?auto=compress&cs=tinysrgb&w=400" },
];

export default function HomePage() {
  const { user } = useAuth();
  const { shippingAddress } = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      let productUrl = `${API_URL}/products?limit=20`;

      const defaultAddr = shippingAddress || (user && user.saved_addresses ? (user.saved_addresses.find(a => a.id === user.default_address_id) || user.saved_addresses[0]) : null);
      if (defaultAddr && defaultAddr.coordinates) {
        const lat = defaultAddr.coordinates.lat;
        const lon = defaultAddr.coordinates.lon || defaultAddr.coordinates.lng;
        if (lat && lon) {
          productUrl += `&user_lat=${lat}&user_lon=${lon}`;
        }
      }

      const [productsRes, categoriesRes, storesRes] = await Promise.all([
        axios.get(productUrl).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/categories`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/stores`).catch(() => ({ data: [] })),
      ]);
      // Only set products if we got some, otherwise keep empty or default to empty
      setProducts(productsRes.data || []);

      if (categoriesRes.data && categoriesRes.data.length > 0) {
        setCategories(categoriesRes.data.map((cat, i) => ({
          ...cat,
          image: defaultCategories[i % defaultCategories.length]?.image,
          icon: defaultCategories[i % defaultCategories.length]?.icon,
        })));
      }
      setStores(storesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      {/* Hero Carousel */}
      <section className="relative h-[350px] md:h-[450px] overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
          >
            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
            <div className={`absolute inset-0 bg-gradient-to-r ${slide.gradient}`}>
              <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl h-full flex items-center">
                <div className="max-w-xl text-white">
                  <h2 className="font-bold text-4xl md:text-5xl lg:text-6xl mb-4">
                    {slide.title}
                  </h2>
                  <p className="text-lg md:text-xl mb-6 opacity-90">{slide.subtitle}</p>
                  <Link to={slide.link}>
                    <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-lg px-8">
                      {slide.cta}
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation */}
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-24 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all" data-testid="hero-prev">
          <ChevronLeft className="h-8 w-8 text-gray-700" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-24 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all" data-testid="hero-next">
          <ChevronRight className="h-8 w-8 text-gray-700" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? "bg-white w-8" : "bg-white/50"
                }`}
            />
          ))}
        </div>

        {/* Gradient fade at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-100 to-transparent" />
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl -mt-16 relative z-10">

        {/* Category Cards Grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {categories.slice(0, 4).map((category) => (
            <Link key={category.id} to={`/catalogue?category=${category.slug}`} data-testid={`category-card-${category.id}`}>
              <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-white h-full group">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3 group-hover:text-orange-600 transition-colors">
                    {category.name}
                  </h3>
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="text-sm text-orange-600 mt-3 font-medium group-hover:underline">
                    Voir tout <ChevronRight className="inline h-4 w-4" />
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </section>

        {/* Flash Deals */}
        <section className="mb-8">
          <Card className="bg-white overflow-hidden shadow-lg">
            <div className="bg-gradient-to-r from-red-600 to-orange-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="h-6 w-6 text-yellow-300 animate-pulse" />
                <h2 className="font-bold text-xl text-white">Ventes Flash du Jour</h2>
                <Badge className="bg-yellow-400 text-black font-bold animate-bounce">JUSQU'À -50%</Badge>
              </div>
              <Link to="/catalogue" className="text-white text-sm hover:underline flex items-center">
                Voir tout <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <CardContent className="p-6">
              <ScrollArea className="w-full">
                <div className="flex gap-4 pb-4">
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <div key={i} className="w-44 flex-shrink-0">
                        <div className="aspect-square bg-gray-200 animate-pulse rounded-lg mb-2" />
                        <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                      </div>
                    ))
                  ) : products.length > 0 ? (
                    products.slice(0, 8).map((product) => (
                      <Link key={product.id} to={`/produit/${product.id}`} className="w-44 flex-shrink-0 group" data-testid={`flash-product-${product.id}`}>
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 mb-2 relative">
                          <img
                            src={product.images?.[0] || "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400"}
                            alt={product.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <Badge className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold">
                            -30%
                          </Badge>
                        </div>
                        <p className="text-sm line-clamp-2 group-hover:text-orange-600 transition-colors font-medium">
                          {product.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-bold text-red-600">{product.price.toFixed(2)} HTG</span>
                          <span className="text-xs text-gray-400 line-through">{(product.price * 1.3).toFixed(2)} HTG</span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-muted-foreground py-8 w-full text-center">Aucune offre disponible</p>
                  )}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </CardContent>
          </Card>
        </section>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Sidebar */}
          <div className="hidden lg:block space-y-4">
            {/* Best Sellers */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-4">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-500" />
                  Meilleures ventes
                </h3>
                <div className="space-y-3">
                  {products.slice(0, 4).map((product, i) => (
                    <Link key={product.id} to={`/produit/${product.id}`} className="flex items-center gap-3 group">
                      <span className="text-2xl font-bold text-orange-500">{i + 1}</span>
                      <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                        <img src={product.images?.[0] || "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=100"} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm line-clamp-1 group-hover:text-orange-600">{product.title}</p>
                        <p className="text-sm font-bold">{product.price.toFixed(2)} HTG</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Categories */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-4">
                <h3 className="font-bold mb-4">Parcourir par catégorie</h3>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <Link key={cat.id} to={`/catalogue?category=${cat.slug}`} className="flex items-center gap-3 p-2 hover:bg-orange-50 rounded-lg transition-colors group">
                      <span className="text-xl">{cat.icon}</span>
                      <span className="text-sm group-hover:text-orange-600">{cat.name}</span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Products Grid */}
          <div className="lg:col-span-3 space-y-6">
            {/* Recommandé pour vous */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-xl flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    Recommandé pour vous
                  </h2>
                  <Link to="/catalogue" className="text-sm text-orange-600 hover:underline">Voir tout</Link>
                </div>
                {loading ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="aspect-square bg-gray-200 animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : products.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {products.slice(0, 4).map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">La marketplace est vide</h3>
                    <p className="text-muted-foreground mb-6">Soyez le premier à vendre !</p>
                    <Link to="/auth">
                      <Button className="bg-orange-500 hover:bg-orange-600">Commencer à vendre</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tous les produits */}
            <Card className="bg-white shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-xl">Tous les produits</h2>
                  <Link to="/catalogue" className="text-sm text-orange-600 hover:underline">Voir le catalogue</Link>
                </div>
                {products.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* More Categories */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {categories.slice(4, 8).map((category) => (
            <Link key={category.id} to={`/catalogue?category=${category.slug}`}>
              <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-white h-full group">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3 group-hover:text-orange-600">{category.name}</h3>
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="text-sm text-orange-600 mt-3 font-medium group-hover:underline">
                    Voir tout <ChevronRight className="inline h-4 w-4" />
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </section>

        {/* Featured Stores */}
        {stores.length > 0 && (
          <section className="mb-8">
            <Card className="bg-white shadow-md">
              <CardContent className="p-6">
                <h2 className="font-bold text-xl mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-orange-500" />
                  Boutiques en vedette
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {stores.slice(0, 4).map((store) => (
                    <Link key={store.id} to={`/boutique/${store.slug}`} className="group">
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-4 text-center">
                          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-2xl font-bold text-orange-600">{store.name.charAt(0)}</span>
                          </div>
                          <h4 className="font-medium group-hover:text-orange-600 line-clamp-1">{store.name}</h4>
                          <p className="text-xs text-gray-500 capitalize">{store.store_type}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Referral CTA */}
        <section className="mb-8">
          <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white overflow-hidden shadow-xl">
            <CardContent className="p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Gift className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="font-bold text-2xl md:text-3xl mb-1">Parrainez vos amis</h2>
                  <p className="text-white/90">Gagnez 5% sur chaque achat de vos filleuls, à vie !</p>
                </div>
              </div>
              <Link to="/dashboard">
                <Button size="lg" variant="secondary" className="font-semibold whitespace-nowrap">
                  Obtenir mon code
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Trust Badges */}
        <section className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Truck, title: "Livraison rapide", desc: "En 2-5 jours ouvrés" },
              { icon: ShieldCheck, title: "Paiement sécurisé", desc: "100% sécurisé" },
              { icon: Clock, title: "Retours gratuits", desc: "30 jours pour changer d'avis" },
              { icon: Users, title: "Support 24/7", desc: "À votre écoute" },
            ].map((item, i) => (
              <Card key={i} className="bg-white shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Seller CTA */}
        <section className="mb-8">
          <Card className="bg-slate-900 text-white overflow-hidden shadow-xl">
            <CardContent className="p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-bold text-2xl md:text-3xl mb-2">Vendez sur BuyMoreEasy</h2>
                <p className="text-gray-300 max-w-lg">
                  Rejoignez des milliers de vendeurs. Commissions avantageuses, outils professionnels et millions de clients potentiels.
                </p>
              </div>
              <Link to="/mes-boutiques">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 font-semibold whitespace-nowrap">
                  Créer ma boutique
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      </div>

      <Footer />
    </div>
  );
}
